import { Link, type LinkProps } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Form } from '@nucleus-commerce/ui/components/form';

import { useProductStock } from '@/features/inventory';
import { StorePricesModal } from '@/features/store-catalog';
import { session } from '@/shared/session/session';

import { useVariantForm } from '../hooks/use-variant-form';

import { SkuHistoryModal } from './sku-history-modal';
import { SkuOriginChip } from './sku-origin-chip';
import {
  VariantBackorderField,
  VariantDeleteButton,
  VariantFulfilmentFields,
  VariantHeaderSwitch,
  VariantImagePicker,
  VariantMoneyField,
  VariantOptionValues,
  VariantShippingFields,
  VariantStatusField,
  VariantTextField,
  VariantToggleField,
} from './variant-form-fields';
import { VariantPageFrame } from './variant-page-frame';

/** What each store holds of the variant, read-only: stock changes on a store's own page. */
function StockByStore({ productId, variant }: { productId: string; variant: Variant }) {
  const { t } = useTranslation(['products', 'inventory']);
  const { can } = usePermissions();
  const readable = can('inventory.read');
  const { me } = session.useSession();
  const stock = useProductStock(productId, readable && variant.trackInventory);
  if (!readable) return null;
  const rows = (me?.stores ?? []).flatMap((store) => {
    const row = stock.data?.find(
      (entry) => entry.storeId === store.id && entry.variantId === variant.id,
    );
    return row ? [{ store, available: row.available }] : [];
  });
  return (
    <div className="grid gap-1.5" data-tour="products.variant.store-stock">
      <span className="text-sm font-medium">{t('products:variantPage2.stockByStore')}</span>
      <div className="text-sm tabular-nums">
        {!variant.trackInventory ? (
          <span className="text-muted-foreground">{t('products:variantPage.notTracked')}</span>
        ) : stock.isPending ? (
          <span className="text-muted-foreground">…</span>
        ) : rows.length === 0 ? (
          <span className="text-muted-foreground">{t('inventory:storeStock.empty')}</span>
        ) : (
          rows.map(({ store, available }, index) => (
            <span key={store.id}>
              {index > 0 ? ' · ' : null}
              <Link
                to="/s/$storeId/inventory/$variantId"
                params={{ storeId: store.id, variantId: variant.id }}
                aria-label={t('inventory:storeStock.open', { store: store.name })}
                className="hover:underline"
              >
                {store.name}
              </Link>{' '}
              {available}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

export interface VariantEditorProps {
  product: Product;
  variant: Variant;
  /** Every variant of the product, in position order. */
  variants: readonly Variant[];
  backTo: LinkProps;
  backLabel: string;
  /** Where another variant of the same product opens. */
  linkTo: (variantId: string) => LinkProps;
  /** The host runs the delete; see `VariantDeleteButton`. */
  onDelete: (variantId: string) => void;
}

/**
 * One variant of the master product on its own page: every field in view, edits are drafts under
 * the save bar (ADR 97). A store's page uses the same form and fields, in its own cards.
 */
export function VariantEditor({
  product,
  variant,
  variants,
  backTo,
  backLabel,
  linkTo,
  onDelete,
}: VariantEditorProps) {
  const { t } = useTranslation('products');
  const format = useFormat();
  const { can } = usePermissions();
  const stockReadable = can('inventory.read');
  const [storePricesOpen, setStorePricesOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const stock = useProductStock(product.id, stockReadable);
  const totals = useMemo(() => {
    const byVariant = new Map<string, number>();
    for (const row of stock.data ?? []) {
      byVariant.set(row.variantId, (byVariant.get(row.variantId) ?? 0) + row.available);
    }
    return byVariant;
  }, [stock.data]);

  const { form, submit } = useVariantForm({ productId: product.id, variant });
  const [price, cost] = useWatch({ control: form.control, name: ['price', 'cost'] });
  const currency = variant.price.currency;
  const profit =
    price && cost && price.amount > 0
      ? {
          amount: format.money({ amount: price.amount - cost.amount, currency }),
          margin: `${Math.round(((price.amount - cost.amount) / price.amount) * 100)}%`,
        }
      : null;

  const linkAction = (label: string, onClick: () => void) => (
    <Button
      type="button"
      variant="link"
      size="xs"
      className="text-primary h-auto p-0 text-[13px]"
      onClick={onClick}
    >
      {label}
    </Button>
  );

  return (
    <VariantPageFrame
      product={product}
      variant={variant}
      variants={variants}
      backTo={backTo}
      backLabel={backLabel}
      linkTo={linkTo}
      railMeta={(row) => ({
        price: row.price,
        stock: stockReadable && row.trackInventory ? (totals.get(row.id) ?? 0) : null,
      })}
    >
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex min-w-0 flex-col gap-4">
          {form.formState.errors.root?.server ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}

          <SummaryCard title={t('variantPage2.variant')}>
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <span className="text-sm font-medium">{t('variantPage2.image')}</span>
                <VariantImagePicker product={product} variant={variant} />
              </div>
              <VariantStatusField />
              <VariantOptionValues product={product} variant={variant} />
            </div>
            {product.options.length > 0 ? (
              <p className="text-muted-foreground text-xs">{t('variantPage.optionsHint')}</p>
            ) : null}
          </SummaryCard>

          <SummaryCard
            title={t('variantPage.price')}
            action={linkAction(t('variantPage2.editStorePrices'), () => setStorePricesOpen(true))}
          >
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <VariantMoneyField
                name="price"
                label={t('variantPage2.priceIn', { currency })}
                currency={currency}
              />
              <VariantMoneyField
                name="compareAt"
                label={t('variantPage2.compareAt')}
                currency={currency}
              />
              <VariantMoneyField
                name="cost"
                label={t('variantPage.costPerItem')}
                hint={t('variantPage.costHint')}
                currency={currency}
              />
              <div className="grid gap-1.5">
                <span className="text-sm font-medium">{t('variantPage2.marginProfit')}</span>
                <span className="py-1.5 text-sm tabular-nums">
                  {profit ? `${profit.margin} · ${profit.amount}` : '—'}
                </span>
              </div>
              <VariantToggleField name="taxable" label={t('variantPage.chargeTax')} />
              <VariantTextField name="taxCode" label={t('variants.sheet.taxCode')} />
            </div>
          </SummaryCard>

          <SummaryCard
            title={t('variantPage.inventory')}
            action={
              <span className="flex items-center gap-3">
                <VariantHeaderSwitch name="trackInventory" label={t('variantPage2.trackStock')} />
                {linkAction(t('sku.history.action'), () => setHistoryOpen(true))}
              </span>
            }
          >
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <StockByStore productId={product.id} variant={variant} />
              <VariantTextField
                name="sku"
                label={t('variants.columns.sku')}
                extra={
                  variant.sku ? (
                    <SkuOriginChip origin={variant.skuIsManual ? 'manual' : 'generated'} />
                  ) : null
                }
              />
              <VariantTextField name="barcode" label={t('variants.columns.barcode')} />
              <VariantBackorderField />
            </div>
          </SummaryCard>

          <SummaryCard
            title={t('variants.sheet.shipping')}
            action={
              <VariantHeaderSwitch name="requiresShipping" label={t('variantPage.physical')} />
            }
          >
            <VariantShippingFields />
          </SummaryCard>

          <SummaryCard title={t('variantPage2.fulfilment')}>
            <VariantFulfilmentFields />
          </SummaryCard>

          <VariantDeleteButton variant={variant} onDelete={onDelete} />
        </form>
      </Form>

      <StorePricesModal
        productId={product.id}
        variant={variant}
        open={storePricesOpen}
        onOpenChange={setStorePricesOpen}
      />
      <SkuHistoryModal productId={product.id} open={historyOpen} onOpenChange={setHistoryOpen} />
    </VariantPageFrame>
  );
}
